import { APP_CONFIG } from '../config/app.config';
import type {
  AuthTokens,
  BackendUser,
  LoginCredentials,
  QuestionnaireAnswerPayload,
  QuestionnaireAttemptState,
  QuestionnaireStartResponse,
  SubmitQuestionnaireResponse,
  SignUpData,
  User,
} from '../types';
import type { InstitutionSignUpPayload, InstitutionSignUpResponse } from '../types/institution.types';
import type { OnboardingData } from '../types';

interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

interface LoginResponse {
  user: User;
  tokens: AuthTokens;
}

interface SignUpResponse {
  user: User;
  tokens: AuthTokens;
}

interface RegisterResponse {
  user_id: number;
  message: string;
}

interface QuestionnaireStartRequest {
  version: string;
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  retryOnUnauthorized?: boolean;
  /** Set true when body is FormData — prevents setting Content-Type so browser can add the multipart boundary */
  skipContentType?: boolean;
}

class ApiService {
  private readonly baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private onAuthFailure: (() => void) | null = null;

  constructor() {
    this.baseUrl = APP_CONFIG.apiUrl;
  }

  setAuthTokens(tokens: AuthTokens | null): void {
    this.accessToken = tokens?.access ?? null;
    this.refreshToken = tokens?.refresh ?? null;
  }

  setAuthFailureHandler(handler: (() => void) | null): void {
    this.onAuthFailure = handler;
  }

  private buildHeaders(options: RequestOptions): Headers {
    const headers = new Headers(options.headers ?? {});

    if (!options.skipContentType && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    if (!options.skipAuth && this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    return headers;
  }

  private normalizeBackendUser(user: BackendUser): User {
    const roleCode = user.roles?.[0]?.code;
    const userType: User['userType'] =
      roleCode === 'teacher' ? 'teacher' : 'student';

    return {
      id: String(user.id),
      name: user.name,
      email: user.email,
      userType,
      institutionCode: user.institution_id ? String(user.institution_id) : undefined,
    };
  }

  private extractErrorMessage(data: unknown, fallback: string): string {
    if (typeof data === 'object' && data !== null) {
      const payload = data as Record<string, unknown>;

      if (typeof payload.error === 'string') {
        return payload.error;
      }

      if (typeof payload.message === 'string') {
        return payload.message;
      }
    }

    return fallback;
  }

  private async parseResponseData(response: Response): Promise<unknown> {
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return null;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  }

  private async performRefresh(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/token/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: this.refreshToken }),
      });

      const data = (await this.parseResponseData(response)) as Partial<AuthTokens> | null;
      if (!response.ok || !data?.access) {
        return false;
      }

      this.accessToken = data.access;
      if (data.refresh) {
        this.refreshToken = data.refresh;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  private async refreshTokens(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh()
      .then((success) => {
        if (!success) {
          this.setAuthTokens(null);
          this.onAuthFailure?.();
        }
        return success;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    try {
      const { retryOnUnauthorized = true } = options;
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: this.buildHeaders(options),
      });

      let data = await this.parseResponseData(response);

      if (
        response.status === 401 &&
        retryOnUnauthorized &&
        !options.skipAuth &&
        this.refreshToken
      ) {
        const refreshed = await this.refreshTokens();
        if (refreshed) {
          return this.request<T>(endpoint, {
            ...options,
            retryOnUnauthorized: false,
          });
        }
      }

      if (!response.ok) {
        const errorMessage = this.extractErrorMessage(
          data,
          `HTTP error! status: ${response.status}`,
        );
        return { error: errorMessage, success: false };
      }

      return { data: data as T, success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { error: errorMessage, success: false };
    }
  }

  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
    const loginResult = await this.request<AuthTokens>('/api/accounts/auth/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
      skipAuth: true,
      retryOnUnauthorized: false,
    });

    if (!loginResult.success || !loginResult.data) {
      return { error: loginResult.error, success: false };
    }

    this.setAuthTokens(loginResult.data);

    const userResult = await this.getCurrentUser();
    if (!userResult.success || !userResult.data) {
      this.setAuthTokens(null);
      return { error: userResult.error || 'Falha ao carregar usuário', success: false };
    }

    return {
      data: {
        user: userResult.data,
        tokens: loginResult.data,
      },
      success: true,
    };
  }

  async signUp(data: SignUpData): Promise<ApiResponse<SignUpResponse>> {
    if (data.password !== data.confirmPassword) {
      return {
        error: 'As senhas não coincidem',
        success: false,
      };
    }

    const role = data.userType === 'teacher' ? 'teacher' : 'student';
    const registerResult = await this.request<RegisterResponse>('/api/accounts/auth/register/', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        password: data.password,
        role,
      }),
      skipAuth: true,
      retryOnUnauthorized: false,
    });

    if (!registerResult.success) {
      return { error: registerResult.error, success: false };
    }

    const loginResult = await this.login({
      email: data.email,
      password: data.password,
    });

    if (!loginResult.success || !loginResult.data) {
      return { error: loginResult.error, success: false };
    }

    return {
      data: {
        user: loginResult.data.user,
        tokens: loginResult.data.tokens,
      },
      success: true,
    };
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.request<BackendUser>('/api/accounts/users/me/', {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      return { error: response.error, success: false };
    }

    return {
      data: this.normalizeBackendUser(response.data),
      success: true,
    };
  }

  async refreshAccessToken(): Promise<ApiResponse<AuthTokens>> {
    const refreshed = await this.refreshTokens();
    if (!refreshed || !this.accessToken || !this.refreshToken) {
      return { error: 'Não foi possível renovar a sessão', success: false };
    }

    return {
      data: {
        access: this.accessToken,
        refresh: this.refreshToken,
      },
      success: true,
    };
  }

  clearSession(): void {
    this.setAuthTokens(null);
  }

  async startQuestionnaire(version: string): Promise<ApiResponse<QuestionnaireStartResponse>> {
    return this.request<QuestionnaireStartResponse>('/api/learning/questionnaires/start/', {
      method: 'POST',
      body: JSON.stringify({ version } satisfies QuestionnaireStartRequest),
    });
  }

  async getQuestionnaireState(
    questionnaireId?: number,
  ): Promise<ApiResponse<QuestionnaireAttemptState>> {
    const path =
      questionnaireId != null
        ? `/api/learning/questionnaires/state/?questionnaire_id=${questionnaireId}`
        : '/api/learning/questionnaires/state/';
    return this.request<QuestionnaireAttemptState>(path, {
      method: 'GET',
    });
  }

  async saveQuestionnaireAnswer(
    attemptId: number,
    answer: QuestionnaireAnswerPayload,
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/api/learning/questionnaires/answers/', {
      method: 'POST',
      body: JSON.stringify({
        attempt_id: attemptId,
        ...answer,
      }),
    });
  }

  async submitQuestionnaire(
    questionnaireId: number,
    answers: QuestionnaireAnswerPayload[],
  ): Promise<ApiResponse<SubmitQuestionnaireResponse>> {
    return this.request<SubmitQuestionnaireResponse>('/api/learning/questionnaires/submit/', {
      method: 'POST',
      body: JSON.stringify({
        questionnaire_id: questionnaireId,
        answers,
      }),
    });
  }

  /** Backend ainda não expõe esta rota; chamadas podem falhar até existir endpoint em Django.
   *  Quando files[] é fornecido, envia multipart/form-data; caso contrário, JSON. */
  async registerInstitution(
    payload: InstitutionSignUpPayload,
    files?: File[]
  ): Promise<ApiResponse<InstitutionSignUpResponse>> {
    if (files && files.length > 0) {
      const formData = new FormData();
      (Object.entries(payload) as [string, unknown][]).forEach(([key, val]) => {
        if (val !== undefined && val !== null) formData.append(key, String(val));
      });
      files.forEach((file) => formData.append('documents', file));
      return this.request<InstitutionSignUpResponse>('/api/institutions/register/', {
        method: 'POST',
        body: formData,
        skipContentType: true,
      });
    }
    return this.request<InstitutionSignUpResponse>('/api/institutions/register/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** Backend ainda não expõe esta rota; usar fluxo local/store até haver API. */
  async saveOnboarding(data: OnboardingData): Promise<ApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>('/api/onboarding/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const apiService = new ApiService();
